/* JournalEntriesTab — searchable list of every manual journal entry NHF has
 * posted (cash donations, checks, grants, sponsorships, wires, reimbursements,
 * opening balances, adjustments). Quick-add modal lives at the top. Each entry
 * can be soft-voided with a reason — never hard-deleted (auditor-friendly).
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  BookOpen, Plus, Loader2, RefreshCw, FileSpreadsheet, Search, ChevronDown, ChevronRight,
  Ban, Coins,
} from 'lucide-react';
import QuickJournalEntry from '../../../components/admin/QuickJournalEntry';
import OpeningBalancesWizard from '../../../components/admin/OpeningBalancesWizard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  posted: 'bg-green-50 text-green-800 border-green-200',
  void: 'bg-gray-100 text-gray-500 border-gray-200',
};

const SOURCE_LABEL = {
  opening_balance: 'Opening balance',
  grant: 'Grant',
  sponsorship: 'Sponsorship',
  check_donation: 'Check donation',
  cash_donation: 'Cash donation',
  wire_transfer: 'Wire / ACH',
  in_kind_donation: 'In-kind',
  reimbursement: 'Reimbursement',
  adjustment: 'Adjustment',
  other: 'Other',
};

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;

const fmtDateTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch (e) { return iso; }
};

const JournalEntriesTab = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: 'posted', source_type: '', search: '' });
  const [openQuick, setOpenQuick] = useState(false);
  const [openOpening, setOpenOpening] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [voiding, setVoiding] = useState(null);
  const [voidReason, setVoidReason] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = new URLSearchParams();
      if (filters.status)      params.set('status', filters.status);
      if (filters.source_type) params.set('source_type', filters.source_type);
      if (filters.search)      params.set('search', filters.search);
      const r = await axios.get(`${BACKEND_URL}/api/admin/journal-entries?${params}`,
        { withCredentials: true });
      setEntries(r.data?.entries || []);
    } catch (e) { setError(e.response?.data?.detail || 'Could not load journal entries.'); }
    setLoading(false);
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const submitVoid = async (entryId) => {
    if (!voidReason.trim()) { setError('Void reason is required.'); return; }
    try {
      await axios.post(`${BACKEND_URL}/api/admin/journal-entries/${entryId}/void`,
        { reason: voidReason.trim() }, { withCredentials: true });
      setVoiding(null); setVoidReason('');
      await load();
    } catch (e) { setError(e.response?.data?.detail || 'Void failed.'); }
  };

  const downloadCsv = () => {
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    window.open(`${BACKEND_URL}/api/admin/journal-entries-export.csv?${params}`, '_blank');
  };

  return (
    <div className="space-y-4" data-testid="journal-entries-tab">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <h2 className="font-heading text-xl font-bold text-[#0A0A0A] inline-flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-[#BA1826]" /> Manual Journal Entries
          </h2>
          <p className="text-xs text-[#4B5563] mt-1 max-w-2xl">
            Every non-Stripe transaction lives here: grants, sponsorships, checks, cash deposits,
            wire transfers, in-kind gifts, reimbursements, and opening balances. Each entry is
            tagged, attributed, and timestamped. Voided entries are kept for the audit trail.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setOpenOpening(true)}
            className="text-xs px-3 py-1.5 border border-[#BA1826]/40 text-[#BA1826] rounded font-semibold inline-flex items-center gap-1.5"
            data-testid="opening-balances-btn">
            <Coins className="w-3 h-3" /> Opening balances
          </button>
          <button onClick={() => setOpenQuick(true)}
            className="text-xs px-3 py-1.5 bg-[#BA1826] text-white rounded font-semibold hover:bg-[#9F1421] inline-flex items-center gap-1.5"
            data-testid="quick-entry-btn">
            <Plus className="w-3 h-3" /> Record transaction
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-3 flex items-center gap-2 flex-wrap"
        data-testid="journal-filters">
        <div className="relative">
          <Search className="w-3 h-3 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <input value={filters.search}
            onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
            placeholder="Search memo or reference…"
            className="pl-7 pr-2 py-1.5 border border-[#E5E7EB] rounded text-xs w-56"
            data-testid="journal-search" />
        </div>
        <select value={filters.status}
          onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
          className="px-2 py-1.5 border border-[#E5E7EB] rounded text-xs"
          data-testid="journal-status-filter">
          <option value="posted">Posted</option>
          <option value="void">Voided</option>
          <option value="">All</option>
        </select>
        <select value={filters.source_type}
          onChange={(e) => setFilters((f) => ({ ...f, source_type: e.target.value }))}
          className="px-2 py-1.5 border border-[#E5E7EB] rounded text-xs"
          data-testid="journal-source-filter">
          <option value="">All sources</option>
          {Object.entries(SOURCE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <button onClick={load}
          className="text-xs px-2.5 py-1.5 border border-[#E5E7EB] rounded inline-flex items-center gap-1.5"
          data-testid="journal-refresh">
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
        <button onClick={downloadCsv}
          className="text-xs px-2.5 py-1.5 border border-[#E5E7EB] rounded inline-flex items-center gap-1.5"
          data-testid="journal-csv-btn">
          <FileSpreadsheet className="w-3 h-3" /> CSV
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-xs p-2 rounded">{error}</div>
      )}

      {/* Entries list */}
      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#BA1826]" /></div>
      ) : entries.length === 0 ? (
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-8 text-center text-sm text-[#9CA3AF]"
          data-testid="journal-empty">
          No journal entries yet. Click <span className="font-bold text-[#BA1826]">Record transaction</span> to
          log your first cash donation, check, grant, or wire.
        </div>
      ) : (
        <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
          {entries.map((e) => (
            <div key={e.id} className={`border-b border-[#E5E7EB] last:border-b-0 ${e.status === 'void' ? 'opacity-60' : ''}`}
              data-testid={`journal-row-${e.id}`}>
              <button type="button"
                onClick={() => setExpanded(expanded === e.id ? null : e.id)}
                className="w-full text-left px-3 py-2.5 hover:bg-[#FAFBFC] flex items-center gap-2">
                {expanded === e.id
                  ? <ChevronDown className="w-3 h-3 text-[#9CA3AF]" />
                  : <ChevronRight className="w-3 h-3 text-[#9CA3AF]" />}
                <span className="font-mono text-xs text-[#9CA3AF] w-24">{e.entry_date}</span>
                <span className={`text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded border ${STATUS_BADGE[e.status] || ''}`}>
                  {e.status}
                </span>
                <span className="text-[10px] uppercase font-semibold tracking-wider text-[#4B5563] px-1.5 py-0.5 rounded bg-[#F1F5F9]">
                  {SOURCE_LABEL[e.source_type] || e.source_type}
                </span>
                <span className="flex-1 truncate text-xs text-[#0A0A0A]">{e.memo}</span>
                <span className="font-mono font-bold text-xs text-[#0A0A0A]">{fmtMoney(e.total_debit)}</span>
              </button>

              {expanded === e.id && (
                <div className="px-3 pb-3 bg-[#FAFBFC] border-t border-[#E5E7EB]" data-testid={`journal-detail-${e.id}`}>
                  <div className="grid grid-cols-2 gap-2 py-2 text-[11px]">
                    {e.reference && <Field label="Reference" value={e.reference} />}
                    <Field label="Posted by" value={e.created_by?.name || e.created_by?.email || '—'} />
                    <Field label="Posted at" value={fmtDateTime(e.created_at)} />
                    {e.voided_at && (
                      <>
                        <Field label="Voided by" value={e.voided_by?.name || e.voided_by?.email || '—'} />
                        <Field label="Voided at" value={fmtDateTime(e.voided_at)} />
                        <Field label="Void reason" value={e.void_reason} colSpan={2} />
                      </>
                    )}
                  </div>

                  {/* Lines table */}
                  <table className="w-full text-[11px] mt-1">
                    <thead className="bg-white border border-[#E5E7EB] text-[#9CA3AF]">
                      <tr>
                        <th className="text-left px-2 py-1 w-16">Code</th>
                        <th className="text-left px-2 py-1">Account</th>
                        <th className="text-left px-2 py-1 hidden md:table-cell">Line memo</th>
                        <th className="text-right px-2 py-1 w-24">Debit</th>
                        <th className="text-right px-2 py-1 w-24">Credit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(e.lines || []).map((ln, i) => (
                        <tr key={i} className="border border-[#E5E7EB] border-t-0 bg-white">
                          <td className="px-2 py-1 font-mono font-bold">{ln.account_code}</td>
                          <td className="px-2 py-1">{ln.account_name || ''}</td>
                          <td className="px-2 py-1 hidden md:table-cell text-[#9CA3AF]">{ln.memo || ''}</td>
                          <td className="px-2 py-1 text-right font-mono">{ln.debit ? fmtMoney(ln.debit) : ''}</td>
                          <td className="px-2 py-1 text-right font-mono">{ln.credit ? fmtMoney(ln.credit) : ''}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-[#0A0A0A] text-white">
                        <td className="px-2 py-1" colSpan={3}><span className="font-bold uppercase tracking-wider text-[9px]">Totals</span></td>
                        <td className="px-2 py-1 text-right font-mono font-bold">{fmtMoney(e.total_debit)}</td>
                        <td className="px-2 py-1 text-right font-mono font-bold">{fmtMoney(e.total_credit)}</td>
                      </tr>
                    </tfoot>
                  </table>

                  {/* Void action */}
                  {e.status === 'posted' && voiding !== e.id && (
                    <div className="mt-2 text-right">
                      <button onClick={() => { setVoiding(e.id); setVoidReason(''); }}
                        className="text-[10px] px-2 py-1 border border-red-200 text-red-700 rounded inline-flex items-center gap-1 hover:bg-red-50"
                        data-testid={`void-btn-${e.id}`}>
                        <Ban className="w-3 h-3" /> Void this entry
                      </button>
                    </div>
                  )}
                  {voiding === e.id && (
                    <div className="mt-2 bg-red-50 border border-red-200 rounded p-2" data-testid={`void-form-${e.id}`}>
                      <p className="text-[11px] font-bold text-red-700 mb-1">Why are you voiding this entry?</p>
                      <textarea value={voidReason} onChange={(ev) => setVoidReason(ev.target.value)}
                        rows={2} placeholder="Required — for the audit trail"
                        className="w-full px-2 py-1 border border-red-200 rounded text-[11px]"
                        data-testid={`void-reason-${e.id}`} />
                      <div className="flex items-center justify-end gap-1.5 mt-1.5">
                        <button onClick={() => { setVoiding(null); setVoidReason(''); }}
                          className="text-[10px] px-2 py-1 border border-[#E5E7EB] rounded text-[#4B5563]">
                          Cancel
                        </button>
                        <button onClick={() => submitVoid(e.id)}
                          className="text-[10px] px-2 py-1 bg-red-600 text-white rounded font-semibold"
                          data-testid={`void-confirm-${e.id}`}>
                          Void entry
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <QuickJournalEntry open={openQuick} onClose={() => setOpenQuick(false)} onSaved={() => load()} />
      <OpeningBalancesWizard open={openOpening} onClose={() => setOpenOpening(false)} onSaved={() => load()} />
    </div>
  );
};

const Field = ({ label, value, colSpan = 1 }) => (
  <div className={colSpan === 2 ? 'col-span-2' : ''}>
    <p className="text-[9px] uppercase font-bold tracking-wider text-[#9CA3AF]">{label}</p>
    <p className="text-[11px] text-[#0A0A0A]">{value}</p>
  </div>
);

export default JournalEntriesTab;
